import { useAuthSelector } from "@risalabs_frontend_org/auth-wrapper";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, openModal } from "risa-oasis-ui_v2";
import BadgeWithIcon from "../../../../../components/badgeWithIcon/badgeWithIcon";
import RenderFormConfigCards from "../../../../../components/formConfigCardsRenderer/renderFormConfigCards";
import { LoaderMessage } from "../../../../../components/loaderMessage/loaderMessage";
import FormConfigModal from "../../../../../components/modals/formConfigModal/formConfigModal";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import {
  FormDataModel,
  FormField,
} from "../../../../../data-model/pharmaPaFormModel";
import { fetchSingleMedicalPaFormConfig } from "../../../../../redux/slice/medicalPa/singleMedicalPaFormConfig";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { FirestoreService } from "../../../../../../shared/api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../../../shared/api/firebase/references";
import {
  BadgeColor,
  BadgeTextColor,
} from "../../../../../../shared/enums/coloredText";
import BackArrowBlue from "../../../../../../shared/svg/back-arrow-blue";
import EditPencilIcon from "../../../../../../shared/svg/editPencil";
import EyeIcon from "../../../../../../shared/svg/eye";
import PlusSquare from "../../../../../../shared/svg/plusSquare";
import { logError } from "../../../../../utils/customLogger";

const SingleFormConfiguration = (props) => {
  const [showSideModalForSection, setShowSideModalForSection] = useState(false);
  const [showFormViewerModal, setShowFormViewerModal] = useState(false);
  const [finalData, setFinalData] = useState<FormDataModel | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();
  const { loading, data } = useSelector(
    (state: RootState) => state.singleMedicalPaFormConfig,
  );
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuthSelector((state) => state.firebaseAuthentication);

  useEffect(() => {
    dispatch(fetchSingleMedicalPaFormConfig(id ?? ""));
  }, [id]);

  useEffect(() => {
    if (isEditing) {
      openModal("form-config-modal");
    }
  }, [isEditing]);

  function flattenArrayFields(fields: FormField[]): FormField[] {
    const deepFlatten = (fields: FormField[]): FormField[] => {
      return fields.map((field) => {
        if (
          (field.type === "section" || field.type === "group") &&
          Array.isArray(field.fields)
        ) {
          return {
            ...field,
            fields: deepFlatten(field.fields),
          };
        }

        if (field.type === "array" && Array.isArray(field.fields)) {
          const flattened = Array.isArray(field.fields[0])
            ? (field.fields as FormField[][]).flat()
            : (field.fields as FormField[]);

          return {
            ...field,
            fields: flattened,
          };
        }

        return field;
      });
    };

    return deepFlatten(fields);
  }

  const handleSaveFormInDb = async () => {
    setIsUpdating(true);
    try {
      await FirestoreService.updateDocument(
        FirestoreCollectionReference.medicalPaFormConfig(),
        id ?? "",
        {
          "data.formTitle": finalData?.formTitle || "",
          "data.fields": flattenArrayFields(finalData?.fields || []),
          updated_at: new Date().toISOString(),
          updated_by: user?.email || "N/A",
        },
      );
      toast.success(ToastMessages.FORM_CONFIGURATION_UPDATE_SUCCESS);
      navigate("/configurations/form-configuration/form-config/");
    } catch (error) {
      logError(error as Error, "Error updating form configuration");
      toast.error(ToastMessages.FORM_CONFIGURATION_UPDATE_FAILURE);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="single-form-configuration--layout flex h-full flex-col gap-4 overflow-hidden">
      {loading ? (
        <>
          <div className="flex h-full w-full items-center justify-center">
            <LoaderMessage message="Loading form configuration..." />
          </div>
        </>
      ) : (
        <>
          <div className="single-form-configuration-header flex items-center justify-between">
            <div className="single-form-configuration-header-left flex items-center gap-3">
              <BackArrowBlue
                height={20}
                width={20}
                stroke="black"
                className="cursor-pointer"
                onClick={() => {
                  navigate("/configurations/form-configuration/form-config/");
                }}
              />
              <span className="text-h11 font-semibold text-primaryGray-1 hover:text-primaryGray-4">
                {data?.data?.formTitle}
              </span>

              <div className="flex gap-2">
                <BadgeWithIcon
                  text={data?.data?.drugName || "--"}
                  id={id || ""}
                  bgColor={BadgeColor.Pending}
                  textColor={BadgeTextColor.Pending}
                />
                <BadgeWithIcon
                  text={data?.data?.providerName || "--"}
                  id={id || ""}
                  bgColor={BadgeColor.New}
                  textColor={BadgeTextColor.New}
                />
              </div>
            </div>

            <div className="single-form-configuration-header-right flex gap-3">
              <EditPencilIcon
                height={20}
                width={20}
                stroke="black"
                className="hover:scale-105 hover:cursor-pointer"
                onClick={() => setIsEditing(true)}
              />
              <PlusSquare
                height={20}
                width={20}
                stroke="black"
                className="hover:scale-105 hover:cursor-pointer"
                onClick={() => {
                  setShowSideModalForSection(true);
                }}
              />
              <EyeIcon
                height={20}
                width={20}
                color="black"
                className="hover:scale-105 hover:cursor-pointer"
                onClick={() => {
                  setShowFormViewerModal(true);
                }}
              />
            </div>
          </div>
          <div className="single-form-configuration-body flex-1 overflow-auto shadow-md">
            <RenderFormConfigCards
              shouldShowSideModalForSection={showSideModalForSection}
              shouldCloseSideModalForSection={(value) => {
                setShowSideModalForSection(!value);
              }}
              shouldShowFormViewerModal={showFormViewerModal}
              shouldCloseFormViewerModal={(value) => {
                setShowFormViewerModal(!value);
              }}
              emitFinalData={(data) => {
                setFinalData(data);
              }}
            />
          </div>
          <div className="single-form-configuration--footer flex items-center justify-end gap-3">
            <div className="mr-auto text-tiny">
              Updated at :{" "}
              <span className="font-semibold">
                {data?.updated_at
                  ? moment(data?.updated_at).format("DD MMM YYYY HH:mm")
                  : "--"}
              </span>{" "}
              by{" "}
              <span className="font-semibold">{data?.updated_by || "--"}</span>
            </div>
            <Button
              buttonType="secondary"
              size="medium"
              disabled={false}
              onClick={() => {
                navigate("/configurations/form-configuration/form-config/");
              }}
            >
              Cancel
            </Button>
            <Button
              buttonType="primary"
              size="medium"
              disabled={isUpdating}
              onClick={handleSaveFormInDb}
            >
              {isUpdating ? "Saving..." : "Save"}
            </Button>
          </div>
          {isEditing && (
            <FormConfigModal
              onClose={() => setIsEditing(false)}
              existingData={{
                formTitle: data?.data?.formTitle || "",
                drugName: data?.data?.drugName || "",
                providerName: data?.data?.providerName || "",
                formId: id ?? "",
                fields: data?.data?.fields || [],
                skip_medical_necessity: data?.skip_medical_necessity || false,
              }}
              isEditing={true}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SingleFormConfiguration;
